import { graphHooks } from '@tunasong/graph-lib/react'
import { Comment } from '@tunasong/icons'
import type { PollOption } from '@tunasong/schemas'
import { IconButton, TextField, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { getOptionLabel } from './option-label.js'
import css from './option-rating.module.css'

export interface OptionCommentButtonProps {
  isOpen: boolean
  onOpen(): void
}

/** Opens the comment field. Hidden once the field shows, since the field itself is then the control. */
export const OptionCommentButton: FC<OptionCommentButtonProps> = props => {
  const { isOpen, onOpen } = props
  if (isOpen) {
    return null
  }
  return (
    <IconButton size="small" aria-label="Add comment" title="Add a comment" className={css.idle} onClick={onOpen}>
      <Comment />
    </IconButton>
  )
}

export interface OptionCommentProps {
  option: PollOption
  comment?: string
  readOnly?: boolean
  /** Whether the voter opened the field; a saved comment always shows. */
  isOpen: boolean
  onClose(): void
  onChange(comment: string): void
}

/** The voter's own note on an option, on its own line under the row. Read-only once the poll closes. */
export const OptionComment: FC<OptionCommentProps> = props => {
  const { option, comment = '', readOnly, isOpen, onClose, onChange } = props
  const { entity } = graphHooks.useEntity(option.entityRef)
  const label = getOptionLabel(option, entity?.name)

  if (readOnly) {
    return comment ? (
      <Typography variant="caption" color="textSecondary" className={css.comment}>
        {comment}
      </Typography>
    ) : null
  }
  if (!(isOpen || comment)) {
    return null
  }
  /** Leaving an empty field closes it again, so an untouched "Add comment" leaves no trace. */
  const handleBlur = () => {
    if (!comment.trim()) {
      onClose()
    }
  }
  return (
    <TextField
      className={css.comment}
      aria-label={`Comment on ${label}`}
      placeholder="Your comment"
      multiline
      rows={2}
      size="small"
      variant="standard"
      maxLength={2000}
      value={comment}
      autoFocus={isOpen && !comment}
      onChange={ev => onChange(ev.target.value)}
      onBlur={handleBlur}
    />
  )
}
